import React, { Component } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import { obfuscatedCoordinates } from '../../util/maps';
import { Heading, Map } from '../../components';

import css from './ListingPage.module.css';

// Helper function to extract suburb and state from full address
const getSuburbAndState = (address) => {
  if (!address) return '';
  
  // Split by commas and trim whitespace
  const parts = address.split(',').map(part => part.trim());
  
  // For Australian addresses, common formats are:
  // "123 Street Name, Suburb, NSW 2000, Australia"
  // "Street Name, Suburb NSW 2000, Australia"
  // "Suburb, NSW, Australia"
  
  if (parts.length >= 4) {
    // Format: "Street, Suburb, State Postcode, Country"
    const suburb = parts[1];
    const stateAndPostcode = parts[2];
    const state = stateAndPostcode.split(' ')[0];
    return `${suburb}, ${state}`;
  } else if (parts.length === 3) {
    // Format: "Street, Suburb State Postcode, Country"
    // The suburb and state are in parts[1]
    const suburbStatePostcode = parts[1];
    
    // Split by spaces to separate suburb, state, and postcode
    const words = suburbStatePostcode.split(' ');
    
    // Australian states are usually: NSW, VIC, QLD, SA, WA, TAS, NT, ACT
    // Or full names: New South Wales, Victoria, Queensland, etc.
    let stateIndex = -1;
    const australianStates = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT', 'New', 'Victoria', 'Queensland', 'South', 'Western', 'Tasmania', 'Northern'];
    
    // Find where the state starts
    for (let i = 0; i < words.length; i++) {
      if (australianStates.includes(words[i])) {
        stateIndex = i;
        break;
      }
    }
    
    if (stateIndex > 0) {
      // Everything before the state is the suburb
      const suburb = words.slice(0, stateIndex).join(' ');
      
      // Handle full state names like "New South Wales"
      let state = '';
      if (words[stateIndex] === 'New' && words[stateIndex + 1] === 'South' && words[stateIndex + 2] === 'Wales') {
        state = 'NSW';
      } else if (words[stateIndex] === 'South' && words[stateIndex + 1] === 'Australia') {
        state = 'SA';
      } else if (words[stateIndex] === 'Western' && words[stateIndex + 1] === 'Australia') {
        state = 'WA';
      } else if (words[stateIndex] === 'Northern' && words[stateIndex + 1] === 'Territory') {
        state = 'NT';
      } else {
        state = words[stateIndex];
      }
      
      return `${suburb}, ${state}`;
    }
    
    // Fallback: just take the first part before any numbers (postcode)
    return suburbStatePostcode.replace(/\s+\d+.*$/, '');
    
  } else if (parts.length === 2) {
    // Format: "Suburb, State" or "Suburb State, Country"
    const suburb = parts[0];
    const stateInfo = parts[1];
    const state = stateInfo.split(' ')[0];
    return `${suburb}, ${state}`;
  }
  
  // If we can't parse it properly, just return the first part (likely suburb)
  return parts[0] || address;
};

/**
 * The SectionMapMaybe component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {propTypes.latlng} [props.geolocation] - The geolocation
 * @param {propTypes.uuid} props.listingId - The listing id
 * @returns {JSX.Element} section map maybe component
 */
class SectionMapMaybe extends Component {
  constructor(props) {
    super(props);
    this.state = { isStatic: true };
  }

  render() {
    const { className, rootClassName, geolocation, publicData, listingId, mapsConfig } = this.props;

    if (!geolocation) {
      return null;
    }

    const address = publicData && publicData.location ? publicData.location.address : '';
    const suburbAndState = getSuburbAndState(address);
    const classes = classNames(rootClassName || css.sectionMap, className);
    const cacheKey = listingId ? `${listingId.uuid}_${geolocation.lat}_${geolocation.lng}` : null;

    const mapProps = mapsConfig.fuzzy.enabled
      ? { 
          obfuscatedCenter: obfuscatedCoordinates(geolocation, mapsConfig.fuzzy.offset, cacheKey),
          zoom: 8 // Zoom out further for wider area view
        }
      : { 
          address, 
          center: geolocation,
          zoom: 8 // Zoom out further for wider area view
        };
    const map = <Map {...mapProps} useStaticMap={this.state.isStatic} />;

    return (
      <section className={classes} id="listing-location">
        <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
          {suburbAndState || <FormattedMessage id="ListingPage.locationTitle" />}
        </Heading>
        {this.state.isStatic ? (
          <button
            className={css.map}
            onClick={() => {
              this.setState({ isStatic: false });
            }}
          >
            {map}
          </button>
        ) : (
          <div className={css.map}>{map}</div>
        )}
      </section>
    );
  }
}

export default SectionMapMaybe;
